import { useState } from "react";
import { Button, Tabs, TabsList, TabsTrigger } from "web-design-system";
import {
  useCreatePortfolioReview,
  usePortfolioReviewOverview,
} from "creso-core/hooks";
import { Client, PortfolioReviewSource } from "creso-core/types";
import { Page } from "@core/components/layout/page";
import { EmptyExternalPortfolio } from "@crm/components/portfolio/no-external-holdings";
import { PortfolioImportStatusComponent } from "@crm/components/portfolio/summary";
import { useNavigate } from "@tanstack/react-router";
import { SparklesIcon } from "lucide-react";
import { handleError } from "@/utils/error";
import { PortfolioJourney } from "./portfolio-journey";
import { PoorlyPerformingFunds } from "./poorly-performing-funds";
import { ExposureSection } from "./exposure-section";
import { ExternalHoldingsSection } from "./external-holdings-section";
import { PastReviews } from "./past-reviews";
import { StartReviewMenu } from "./start-review-menu";

export function PortfolioReviewLanding({ client }: { client: Client }) {
  const clientId = client.id;
  const investmentAccountId = client.investment_account ?? "";
  const [source, setSource] = useState<PortfolioReviewSource>("ALL");
  const { data: overview, isLoading } = usePortfolioReviewOverview({
    investment_account_guid: investmentAccountId,
    source,
  });

  // Nothing to review for this source until holdings are in.
  const header = overview?.header;
  const hasNoAum = !!header?.ok && !Number(header.data?.current_value);
  // With no holdings at all there is nothing to switch between or review.
  // Kept on an empty Internal/External tab so the user can switch back.
  const isEmptyAccount = hasNoAum && source === "ALL";

  const { mutateAsync: createReview, isPending } = useCreatePortfolioReview();

  const navigate = useNavigate();

  return (
    <>
      <Page.Header
        title="Portfolio Review"
        actions={
          isEmptyAccount ? null : (
            <>
              <Button
                disabled={isPending}
                onClick={() =>
                  createReview({
                    investment_account_guid: investmentAccountId,
                    mode: "AUTOMATED",
                    source: "ALL",
                  })
                    .then((review) =>
                      navigate({
                        to: "/clients/$id/review/$reviewId",
                        params: {
                          id: String(clientId),
                          reviewId: String(review.id),
                        },
                      })
                    )
                    .catch((e) => handleError(e, "Failed to generate review"))
                }
                variant="outline"
              >
                <SparklesIcon />
                Creso Recommended
              </Button>
              <StartReviewMenu
                clientId={clientId}
                investmentAccountId={investmentAccountId}
              />
            </>
          )
        }
      />
      <Page.Body className="flex flex-col gap-10 pb-8">
        <div className="flex flex-1 flex-col gap-6">
          {isEmptyAccount ? null : (
            <Tabs
              value={source}
              onValueChange={(v) => setSource(v as PortfolioReviewSource)}
            >
              <div className="flex flex-row items-center justify-between">
                <TabsList>
                  <TabsTrigger value="ALL">All</TabsTrigger>
                  <TabsTrigger value="INTERNAL">Internal</TabsTrigger>
                  <TabsTrigger value="EXTERNAL">External</TabsTrigger>
                </TabsList>
                <PortfolioImportStatusComponent
                  selectedTab={source === "ALL" ? "OVERALL" : source}
                  client={client}
                />
              </div>
            </Tabs>
          )}
          {hasNoAum ? (
            <EmptyExternalPortfolio client={client} />
          ) : (
            <>
              <PortfolioJourney
                investmentAccountId={investmentAccountId}
                source={source}
              />
              <PoorlyPerformingFunds
                block={overview?.poorly_performing}
                isLoading={isLoading}
              />
              <ExposureSection
                block={overview?.exposure}
                isLoading={isLoading}
              />
              {/* Holdings under other brokers say nothing about internal ones. */}
              {source !== "INTERNAL" ? (
                <ExternalHoldingsSection
                  block={overview?.external_holdings}
                  isLoading={isLoading}
                />
              ) : null}
            </>
          )}
        </div>
        {isEmptyAccount ? null : <PastReviews clientId={clientId} />}
      </Page.Body>
    </>
  );
}
